import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Grid,
  Group,
  Loader,
  Modal,
  PasswordInput,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip,
  ActionIcon,
  ScrollArea,
  SimpleGrid,
  Table,
  Switch,
  Textarea,
  NumberInput,
  SegmentedControl,
  Checkbox,
} from '@mantine/core';
import { notifySuccess } from '../lib/notify';
import { translateStatic } from '../i18n/translations';
import { useTranslation } from '../i18n/LocaleContext';
import { useForm } from '@mantine/form';
import {
  IconArrowLeft,
  IconAlertCircle,
  IconRefresh,
  IconEye,
  IconKey,
  IconShieldLock,
  IconWallet,
  IconBan,
  IconCheck,
  IconCopy,
  IconTrash,
  IconPlus,
  IconPlugConnected,
  IconCreditCard,
  IconCoin,
  IconDeviceFloppy,
  IconExternalLink,
} from '@tabler/icons-react';
import { merchantsApi } from '../api/merchants';
import { walletApi } from '../api/wallet';
import {
  type MerchantDto,
  type WalletDto,
} from '../api/types';
import { ApiCallError, describeError } from '../api/client';
import { usePermissions } from '../auth/SessionContext';

type TokenReveal =
  | { kind: 'rotated'; clientId: string; clientToken: string; webhookSecret?: string }
  | { kind: 'revealed'; clientId: string; clientToken: string; webhookSecret?: string };
import { formatGmt7, formatIdr, isIpv4 } from '../lib/format';

// Lax UUID regex — server-side validates strictly; here we only need to
// avoid hitting the API with obviously bad input.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-9][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function MerchantDetailPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const merchantId = id;
  const queryClient = useQueryClient();

  const { data: merchant, isLoading, error } = useQuery({
    queryKey: ['merchant', merchantId],
    queryFn: () => merchantsApi.get(merchantId),
    enabled: UUID_RE.test(merchantId),
    retry: 0,
  });

  const { has, isSuperuser } = usePermissions();
  const canManageCreds = isSuperuser || has('merchants.credentials.manage');
  const canSuspend = isSuperuser || has('merchants.suspend');
  const canUnsuspend = isSuperuser || has('merchants.suspend.reset');
  const canViewPaymentMethods = isSuperuser || has('merchants.payment_methods.view');
  const canManagePaymentMethods = isSuperuser || has('merchants.payment_methods.manage');
  const canManageApiDisbursement = isSuperuser || has('merchants.api_disbursement.manage');
  const canEditMerchant = isSuperuser || has('merchants.edit');

  const [tokenReveal, setTokenReveal] = useState<TokenReveal | null>(null);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [pwPromptOpen, setPwPromptOpen] = useState(false);
  const [pwInput, setPwInput] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);

  const suspendMut = useMutation({
    meta: { successKey: 'notify.merchant.suspend.ok', errorKey: 'notify.merchant.suspend.fail' },
    mutationFn: () => merchantsApi.suspend(merchantId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['merchant', merchantId] }),
  });
  const unsuspendMut = useMutation({
    meta: { successKey: 'notify.merchant.unsuspend.ok', errorKey: 'notify.merchant.unsuspend.fail' },
    mutationFn: () => merchantsApi.unsuspend(merchantId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['merchant', merchantId] }),
  });
  const regenMut = useMutation({
    meta: { successKey: 'notify.merchant.regenCreds.ok', errorKey: 'notify.merchant.regenCreds.fail' },
    mutationFn: () => merchantsApi.regenerateCredentials(merchantId),
    onSuccess: (data) => {
      setRevealError(null);
      setTokenReveal({
        kind: 'rotated',
        clientId: data.credentials.transpayClientId,
        clientToken: data.credentials.transpayClientToken,
        webhookSecret: data.webhookSecret,
      });
    },
  });
  const revealMut = useMutation({
    meta: { successNotification: false, errorNotification: false }, // reveal shows the token in UI; not a "save"; errors handled inline
    mutationFn: (password: string) => merchantsApi.revealCredentials(merchantId, password),
    onSuccess: (data) => {
      setRevealError(null);
      setPwError(null);
      setPwPromptOpen(false);
      setPwInput('');
      setTokenReveal({
        kind: 'revealed',
        clientId: data.transpayClientId,
        clientToken: data.transpayClientToken,
        webhookSecret: data.webhookSecret,
      });
    },
    onError: (err) => {
      if (err instanceof ApiCallError) {
        if (err.code === 'invalid_password') {
          setPwError(t('merchantDetail.errPasswordWrong'));
          return;
        }
        if (err.code === 'token_not_revealable') {
          setPwPromptOpen(false);
          setRevealError(t('merchantDetail.errTokenNotRevealable'));
          return;
        }
        if (err.code === 'kms_decrypt_failed') {
          setPwPromptOpen(false);
          setRevealError(t('merchantDetail.errKmsDecrypt'));
          return;
        }
      }
      setPwPromptOpen(false);
      setRevealError((err as Error).message);
    },
  });

  const openPasswordPrompt = () => {
    setPwInput('');
    setPwError(null);
    setRevealError(null);
    setPwPromptOpen(true);
  };
  const submitPasswordPrompt = () => {
    if (!pwInput) {
      setPwError(t('merchantDetail.errPasswordRequired'));
      return;
    }
    revealMut.mutate(pwInput);
  };

  if (isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }
  if (error || !merchant) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />}>
        {describeError(error)}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="sm">
          <Button component={Link} to="/dashboard/merchants" variant="default" leftSection={<IconArrowLeft size={14} />}>
            {t('merchantDetail.back')}
          </Button>
          <Title order={2}>{merchant.name}</Title>
          {merchant.suspendedAt ? (
            <Badge color="red">{t('merchantDetail.badgeSuspended')}</Badge>
          ) : (
            <Badge color="green">{t('merchantDetail.badgeActive')}</Badge>
          )}
        </Group>
        <Group gap="xs">
          {merchant.suspendedAt
            ? canUnsuspend && (
                <Button
                  variant="default"
                  onClick={() => unsuspendMut.mutate()}
                  loading={unsuspendMut.isPending}
                  leftSection={<IconCheck size={14} />}
                >
                  {t('merchantDetail.unsuspend')}
                </Button>
              )
            : canSuspend && (
                <Button
                  variant="default"
                  color="red"
                  onClick={() => suspendMut.mutate()}
                  loading={suspendMut.isPending}
                  leftSection={<IconBan size={14} />}
                >
                  {t('merchantDetail.suspend')}
                </Button>
              )}
          {canManageCreds && (
            <Button
              variant="default"
              leftSection={<IconEye size={14} />}
              onClick={openPasswordPrompt}
              loading={revealMut.isPending}
              disabled={!merchant.transpayClientTokenSuffix}
            >
              {t('merchantDetail.revealToken')}
            </Button>
          )}
          <Button onClick={() => regenMut.mutate()} loading={regenMut.isPending} leftSection={<IconRefresh size={14} />}>
            {t('merchantDetail.regenerateCredentials')}
          </Button>
        </Group>
      </Group>

      {revealError && (
        <Alert color="yellow" icon={<IconAlertCircle size={16} />} variant="light">
          {revealError}
        </Alert>
      )}

      <Tabs defaultValue="info">
        <Tabs.List>
          <Tabs.Tab value="info">{t('merchantDetail.tabInfo')}</Tabs.Tab>
          <Tabs.Tab value="credentials" leftSection={<IconKey size={14} />}>
            {t('merchantDetail.tabCredentials')}
          </Tabs.Tab>
          <Tabs.Tab value="ip-whitelist" leftSection={<IconShieldLock size={14} />}>
            {t('merchantDetail.tabIpWhitelist')}
          </Tabs.Tab>
          <Tabs.Tab value="payok" leftSection={<IconPlugConnected size={14} />}>
            {t('merchantDetail.tabPayok')}
          </Tabs.Tab>
          {canViewPaymentMethods && (
            <Tabs.Tab value="payment-methods" leftSection={<IconCreditCard size={14} />}>
              {t('merchantDetail.tabPaymentMethods')}
            </Tabs.Tab>
          )}
          {canEditMerchant && (
            <Tabs.Tab value="va-fees" leftSection={<IconCoin size={14} />}>
              {t('merchantDetail.vaFeesTab')}
            </Tabs.Tab>
          )}
          {canEditMerchant && (
            <Tabs.Tab value="disbursement-fees" leftSection={<IconCoin size={14} />}>
              {t('merchantDetail.disbursementFeesTab')}
            </Tabs.Tab>
          )}
          <Tabs.Tab value="wallet" leftSection={<IconWallet size={14} />}>
            {t('merchantDetail.tabWallet')}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="info" pt="md">
          <MerchantInfoEditPanel merchant={merchant} />
        </Tabs.Panel>

        <Tabs.Panel value="credentials" pt="md">
          <Card>
            <Stack>
              <Group>
                <Text fw={600}>{t('merchantDetail.transpayClientId')}</Text>
                <Code>{merchant.transpayClientId}</Code>
                <CopyButton value={merchant.transpayClientId} timeout={1500}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? t('merchantDetail.copied') : t('merchantDetail.copy')}>
                      <ActionIcon variant="default" onClick={copy}>
                        {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
              <Group>
                <Text fw={600}>{t('merchantDetail.tokenSuffix')}</Text>
                <Code>{merchant.transpayClientTokenSuffix ?? '—'}</Code>
                <Text c="dimmed" size="xs">
                  {t('merchantDetail.tokenSuffixHint')} {canManageCreds
                    ? t('merchantDetail.tokenSuffixHintAdmin')
                    : t('merchantDetail.tokenSuffixHintNonAdmin')}
                </Text>
              </Group>
            </Stack>
          </Card>
          {canManageApiDisbursement && <ApiDisbursementCard merchant={merchant} />}
        </Tabs.Panel>

        <Tabs.Panel value="ip-whitelist" pt="md">
          <ApiIpWhitelistPanel merchantId={merchantId} />
        </Tabs.Panel>

        <Tabs.Panel value="payok" pt="md">
          <PayokSettingsPanel merchant={merchant} />
        </Tabs.Panel>

        {canViewPaymentMethods && (
          <Tabs.Panel value="payment-methods" pt="md">
            <MerchantPaymentMethodsPanel
              merchantId={merchantId}
              canManage={canManagePaymentMethods}
            />
          </Tabs.Panel>
        )}

        {canEditMerchant && (
          <Tabs.Panel value="va-fees" pt="md">
            <VaFeesPanel merchant={merchant} canEdit={canEditMerchant} />
          </Tabs.Panel>
        )}

        {canEditMerchant && (
          <Tabs.Panel value="disbursement-fees" pt="md">
            <DisbursementFeesPanel merchant={merchant} canEdit={canEditMerchant} />
          </Tabs.Panel>
        )}

        <Tabs.Panel value="wallet" pt="md">
          <WalletPanel merchantId={merchantId} />
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={pwPromptOpen}
        onClose={() => {
          if (!revealMut.isPending) setPwPromptOpen(false);
        }}
        title={t('merchantDetail.verifyPasswordTitle')}
        size="md"
        closeOnClickOutside={!revealMut.isPending}
      >
        <Stack>
          <Text size="sm" c="dimmed">
            {t('merchantDetail.verifyPasswordIntro')}
          </Text>
          <PasswordInput
            label={t('merchantDetail.accountPasswordLabel')}
            placeholder="••••••••"
            value={pwInput}
            onChange={(e) => setPwInput(e.currentTarget.value)}
            error={pwError ?? undefined}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitPasswordPrompt();
            }}
            disabled={revealMut.isPending}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setPwPromptOpen(false)}
              disabled={revealMut.isPending}
            >
              {t('merchantDetail.cancel')}
            </Button>
            <Button onClick={submitPasswordPrompt} loading={revealMut.isPending}>
              {t('merchantDetail.revealToken')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={tokenReveal !== null}
        onClose={() => setTokenReveal(null)}
        title={
          tokenReveal?.kind === 'rotated'
            ? t('merchantDetail.tokenRotatedTitle')
            : t('merchantDetail.tokenPlaintextTitle')
        }
        size="lg"
        closeOnClickOutside={false}
      >
        {tokenReveal && (
          <Stack gap="md">
            <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
              {tokenReveal.kind === 'rotated'
                ? t('merchantDetail.tokenRotatedWarning')
                : t('merchantDetail.tokenRevealedWarning')}
            </Alert>
            <RevealField label={t('merchantDetail.transpayClientId')} value={tokenReveal.clientId} />
            <RevealField label={t('merchantDetail.transpayClientToken')} value={tokenReveal.clientToken} />
            {tokenReveal.webhookSecret && (
              <RevealField
                label={t('merchantDetail.webhookSecret')}
                value={tokenReveal.webhookSecret}
              />
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setTokenReveal(null)}>
                {t('merchantDetail.close')}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <Grid.Col span={{ base: 12, md: 6 }}>
      <Stack gap={2}>
        <Text c="dimmed" size="xs" tt="uppercase" fw={600}>
          {label}
        </Text>
        <Text size="sm">{value ?? '—'}</Text>
      </Stack>
    </Grid.Col>
  );
}

function RevealField({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation();
  return (
    <Stack gap={4}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Group gap="xs">
        <Code style={{ flex: 1, fontSize: 12, padding: '6px 10px', wordBreak: 'break-all' }}>
          {value}
        </Code>
        <CopyButton value={value} timeout={1500}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? t('merchantDetail.copied') : t('merchantDetail.copy')}>
              <ActionIcon variant={copied ? 'filled' : 'default'} color={copied ? 'green' : 'gray'} onClick={copy}>
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
    </Stack>
  );
}

function ApiIpWhitelistPanel({ merchantId }: { merchantId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['merchant-ip-whitelist', merchantId],
    queryFn: () => merchantsApi.listApiIpWhitelist(merchantId),
    retry: 0,
  });
  const form = useForm({
    initialValues: { cidr: '', label: '' },
    validate: {
      cidr: (v) =>
        v.trim().length === 0
          ? t('merchantDetail.ipwlRequired')
          : isIpv4(v)
            ? null
            : t('merchantDetail.ipwlInvalidIp'),
      label: (v) => (v.trim().length === 0 ? t('merchantDetail.ipwlRequired') : null),
    },
  });
  const addMut = useMutation({
    meta: { successKey: 'notify.ipwl.add.ok', errorKey: 'notify.ipwl.add.fail' },
    mutationFn: (v: { cidr: string; label: string }) => merchantsApi.addApiIpWhitelist(merchantId, v),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-ip-whitelist', merchantId] });
      form.reset();
    },
  });
  const removeMut = useMutation({
    meta: { successKey: 'notify.ipwl.remove.ok', errorKey: 'notify.ipwl.remove.fail' },
    mutationFn: (rowId: number) => merchantsApi.removeApiIpWhitelist(merchantId, rowId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['merchant-ip-whitelist', merchantId] }),
  });

  return (
    <Stack gap="md">
      <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
        {t('merchantDetail.ipwlDenyAll')}
      </Alert>

      <Card>
        <Text fw={600} mb="sm">
          {t('merchantDetail.ipwlAddCidr')}
        </Text>
        <form onSubmit={form.onSubmit((v) => addMut.mutate(v))}>
          <Group align="end">
            <TextInput
              label={t('merchantDetail.ipwlCidrLabel')}
              placeholder={t('merchantDetail.ipwlCidrPlaceholder')}
              style={{ flex: 1 }}
              {...form.getInputProps('cidr')}
            />
            <TextInput
              label={t('merchantDetail.ipwlLabelLabel')}
              placeholder={t('merchantDetail.ipwlLabelPlaceholder')}
              style={{ flex: 1 }}
              {...form.getInputProps('label')}
            />
            <Button type="submit" loading={addMut.isPending} leftSection={<IconPlus size={14} />}>
              {t('merchantDetail.ipwlAddCidr')}
            </Button>
          </Group>
        </form>
      </Card>

      <Card p={0}>
        {isLoading ? (
          <Group justify="center" py="lg">
            <Loader />
          </Group>
        ) : (
          <ScrollArea>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('merchantDetail.ipwlColCidr')}</Table.Th>
                  <Table.Th>{t('merchantDetail.ipwlColLabel')}</Table.Th>
                  <Table.Th>{t('merchantDetail.ipwlColCreatedBy')}</Table.Th>
                  <Table.Th>{t('merchantDetail.ipwlColCreated')}</Table.Th>
                  <Table.Th>{t('merchantDetail.ipwlColLastUsed')}</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data?.rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="dimmed" ta="center" py="lg">
                        {t('merchantDetail.ipwlEmpty')}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {data?.rows.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td>
                      <Code>{row.cidr}</Code>
                    </Table.Td>
                    <Table.Td>{row.label}</Table.Td>
                    <Table.Td>{row.createdBy}</Table.Td>
                    <Table.Td>{formatGmt7(row.createdAt)}</Table.Td>
                    <Table.Td>{formatGmt7(row.lastUsedAt)}</Table.Td>
                    <Table.Td>
                      <Tooltip label={t('merchantDetail.ipwlRemove')}>
                        <ActionIcon
                          variant="default"
                          color="red"
                          onClick={() => removeMut.mutate(row.id)}
                          loading={removeMut.isPending && removeMut.variables === row.id}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Card>
    </Stack>
  );
}

/**
 * Placeholder merchant-svc returns for fields the viewer may not read. The
 * webhook URLs are only masked for viewers without `merchants.edit`; if it
 * ever shows up here we must not echo it back into the PATCH body (it would
 * fail the backend `url()` validation and could overwrite the stored URL).
 */
const MASKED_VALUE = '***MASKED***';
const isMasked = (v: string | null | undefined) => v === MASKED_VALUE;

/** Empty is allowed (clears the URL); otherwise it must parse as http(s). */
function validateOptionalHttpUrl(v: string, message: string): string | null {
  const trimmed = v.trim();
  if (trimmed === '') return null;
  try {
    const u = new URL(trimmed);
    return u.protocol === 'http:' || u.protocol === 'https:' ? null : message;
  } catch {
    return message;
  }
}

function MerchantInfoEditPanel({ merchant }: { merchant: MerchantDto }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const webhookUrlsMasked =
    isMasked(merchant.depositWebhookUrl) || isMasked(merchant.disbursementWebhookUrl);
  const form = useForm({
    initialValues: {
      name: merchant.name,
      email: merchant.email,
      depositFeeBps: merchant.depositFeeBps,
      disbursementFeeIdr: merchant.disbursementFeeIdr,
      cashinMinAmount: merchant.cashinMinAmount,
      cashinMaxAmount: merchant.cashinMaxAmount,
      cashoutMinAmount: merchant.cashoutMinAmount,
      cashoutMaxAmount: merchant.cashoutMaxAmount,
      apiAccessEnabled: merchant.apiAccessEnabled,
      depositWebhookUrl: webhookUrlsMasked ? '' : (merchant.depositWebhookUrl ?? ''),
      disbursementWebhookUrl: webhookUrlsMasked ? '' : (merchant.disbursementWebhookUrl ?? ''),
    },
    validate: {
      name: (v) => (v.trim().length === 0 ? t('merchantDetail.valRequired') : null),
      depositWebhookUrl: (v) => validateOptionalHttpUrl(v, t('merchantDetail.valInvalidUrl')),
      disbursementWebhookUrl: (v) => validateOptionalHttpUrl(v, t('merchantDetail.valInvalidUrl')),
      email: (v) => (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) ? null : t('merchantDetail.valInvalidEmail')),
      depositFeeBps: (v) => (v < 0 || v > 10000 ? t('merchantDetail.valDepositFeeRange') : null),
      disbursementFeeIdr: (v) => (v < 0 || v > 999_999_999 ? t('merchantDetail.valDisbFeeRange') : null),
      cashinMinAmount: (v) => (v < 1 ? t('merchantDetail.valMinRp1') : null),
      cashinMaxAmount: (v, vals) =>
        v < 1 ? t('merchantDetail.valMinRp1') : v < vals.cashinMinAmount ? t('merchantDetail.valMaxGteMin') : null,
      cashoutMinAmount: (v) => (v < 1 ? t('merchantDetail.valMinRp1') : null),
      cashoutMaxAmount: (v, vals) =>
        v < 1 ? t('merchantDetail.valMinRp1') : v < vals.cashoutMinAmount ? t('merchantDetail.valMaxGteMin') : null,
    },
  });

  const mutation = useMutation({
    meta: { successKey: 'notify.merchant.save.ok', errorKey: 'notify.merchant.save.fail' },
    mutationFn: () =>
      merchantsApi.patch(merchant.id, {
        name: form.values.name.trim(),
        email: form.values.email.trim(),
        depositFeeBps: form.values.depositFeeBps,
        disbursementFeeIdr: form.values.disbursementFeeIdr,
        cashinMinAmount: form.values.cashinMinAmount,
        cashinMaxAmount: form.values.cashinMaxAmount,
        cashoutMinAmount: form.values.cashoutMinAmount,
        cashoutMaxAmount: form.values.cashoutMaxAmount,
        apiAccessEnabled: form.values.apiAccessEnabled,
        // Only send the webhook URLs when we were given the real values;
        // otherwise leave them untouched server-side.
        ...(webhookUrlsMasked
          ? {}
          : {
              depositWebhookUrl: form.values.depositWebhookUrl.trim() || null,
              disbursementWebhookUrl: form.values.disbursementWebhookUrl.trim() || null,
            }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['merchant', merchant.id] }),
  });

  const dirty = form.isDirty();

  return (
    <Stack gap="md">
      {/* Immutable identity — surfaced first, never editable */}
      <Card>
        <Stack gap="sm">
          <Text fw={600}>{t('merchantDetail.identityReadOnly')}</Text>
          <Grid>
            <Field label={t('merchantDetail.fieldMerchantId')} value={String(merchant.id)} />
            <Field label={t('merchantDetail.fieldCreated')} value={formatGmt7(merchant.createdAt)} />
            <Field label={t('merchantDetail.fieldLastUpdated')} value={formatGmt7(merchant.updatedAt)} />
            <Field
              label={t('merchantDetail.fieldStatus')}
              value={
                merchant.suspendedAt
                  ? t('merchantDetail.statusSuspendedAt', { at: formatGmt7(merchant.suspendedAt) })
                  : t('merchantDetail.statusActive')
              }
            />
          </Grid>
        </Stack>
      </Card>

      <form onSubmit={form.onSubmit(() => mutation.mutate())}>
        <Stack gap="md">
          <Card>
            <Stack gap="sm">
              <Text fw={600}>{t('merchantDetail.basics')}</Text>
              <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput label={t('merchantDetail.nameLabel')} required {...form.getInputProps('name')} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput label={t('merchantDetail.emailLabel')} required {...form.getInputProps('email')} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Switch
                    label={t('merchantDetail.apiAccessEnabled')}
                    description={t('merchantDetail.apiAccessEnabledDesc')}
                    mt="md"
                    {...form.getInputProps('apiAccessEnabled', { type: 'checkbox' })}
                  />
                </Grid.Col>
              </Grid>
            </Stack>
          </Card>

          <Card>
            <Stack gap="sm">
              <Text fw={600}>{t('merchantDetail.fees')}</Text>
              <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <NumberInput
                    label={t('merchantDetail.depositFeeBps')}
                    description={t('merchantDetail.depositFeeBpsDesc', { pct: (form.values.depositFeeBps / 100).toFixed(2) })}
                    min={0}
                    max={10000}
                    {...form.getInputProps('depositFeeBps')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <NumberInput
                    label={t('merchantDetail.disbursementFeeIdr')}
                    description={t('merchantDetail.disbursementFeeIdrDesc')}
                    min={0}
                    max={999_999_999}
                    thousandSeparator=","
                    {...form.getInputProps('disbursementFeeIdr')}
                  />
                </Grid.Col>
              </Grid>
            </Stack>
          </Card>

          <Card>
            <Stack gap="sm">
              <Text fw={600}>{t('merchantDetail.transactionLimits')}</Text>
              <Text size="xs" c="dimmed">
                {t('merchantDetail.transactionLimitsDesc')}
              </Text>
              <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <NumberInput
                    label={t('merchantDetail.cashinMin')}
                    description={t('merchantDetail.cashinMinDesc')}
                    min={1}
                    max={100_000_000_000}
                    thousandSeparator=","
                    {...form.getInputProps('cashinMinAmount')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <NumberInput
                    label={t('merchantDetail.cashinMax')}
                    description={t('merchantDetail.cashinMaxDesc')}
                    min={1}
                    max={100_000_000_000}
                    thousandSeparator=","
                    {...form.getInputProps('cashinMaxAmount')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <NumberInput
                    label={t('merchantDetail.cashoutMin')}
                    description={t('merchantDetail.cashoutMinDesc')}
                    min={1}
                    max={100_000_000_000}
                    thousandSeparator=","
                    {...form.getInputProps('cashoutMinAmount')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <NumberInput
                    label={t('merchantDetail.cashoutMax')}
                    description={t('merchantDetail.cashoutMaxDesc')}
                    min={1}
                    max={100_000_000_000}
                    thousandSeparator=","
                    {...form.getInputProps('cashoutMaxAmount')}
                  />
                </Grid.Col>
              </Grid>
            </Stack>
          </Card>

          <Card>
            <Stack gap="sm">
              <Text fw={600}>{t('merchantDetail.webhookUrls')}</Text>
              <Text size="xs" c="dimmed">
                {t('merchantDetail.webhookUrlsDesc')}
              </Text>
              <TextInput
                label={t('merchantDetail.depositWebhookUrl')}
                placeholder="https://merchant.com/webhooks/transpay/deposit"
                disabled={webhookUrlsMasked}
                {...form.getInputProps('depositWebhookUrl')}
              />
              <TextInput
                label={t('merchantDetail.disbursementWebhookUrl')}
                placeholder="https://merchant.com/webhooks/transpay/disbursement"
                disabled={webhookUrlsMasked}
                {...form.getInputProps('disbursementWebhookUrl')}
              />
            </Stack>
          </Card>

          {mutation.isSuccess && !dirty && (
            <Alert color="green" icon={<IconCheck size={16} />}>
              {t('merchantDetail.changesSaved')}
            </Alert>
          )}

          <Group justify="flex-end">
            <Button
              variant="default"
              disabled={!dirty}
              onClick={() => form.reset()}
            >
              {t('merchantDetail.reset')}
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
              disabled={!dirty}
              leftSection={<IconDeviceFloppy size={14} />}
            >
              {t('merchantDetail.saveChanges')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Stack>
  );
}

function PayokSettingsPanel({ merchant }: { merchant: MerchantDto }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { has, isSuperuser } = usePermissions();
  const canEditMerchant = isSuperuser || has('merchants.edit');
  // merchant-svc returns `payok: null` to viewers without `merchants.edit`.
  // Never render the toggle from a null block: the form would default to
  // "disabled" and a save would silently switch PAYOK off for the merchant.
  const p = merchant.payok;

  // PAYOK connection config (base URL, MID, endpoints, signing keys, notification
  // URLs) is no longer per-merchant — it lives centrally on the Acquirer and is
  // managed from the Acquirer menu. The only thing settable per merchant is the
  // on/off toggle (payokEnabled); the backend ignores every other PAYOK field.
  const form = useForm({
    initialValues: { enabled: p?.enabled ?? false },
  });

  const mutation = useMutation({
    meta: { successKey: 'notify.merchant.save.ok', errorKey: 'notify.merchant.save.fail' },
    mutationFn: () => merchantsApi.patch(merchant.id, { payok: { enabled: form.values.enabled } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['merchant', merchant.id] }),
  });

  if (p === null) {
    return (
      <Alert color="gray" icon={<IconPlugConnected size={16} />} variant="light">
        {t('merchantDetail.payokUnavailable')}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Alert
        color={p.enabled ? 'green' : 'yellow'}
        icon={<IconPlugConnected size={16} />}
        variant="light"
      >
        <Text fw={600} size="sm">
          {t('merchantDetail.payokEnabledTitle', {
            state: p.enabled
              ? t('merchantDetail.payokStateEnabled')
              : t('merchantDetail.payokStateDisabled'),
          })}
        </Text>
      </Alert>

      <Alert color="blue" icon={<IconExternalLink size={16} />} variant="light">
        {t('merchantDetail.payokManagedNote')}
      </Alert>

      <form onSubmit={form.onSubmit(() => mutation.mutate())}>
        <Card>
          <Stack gap="sm">
            <Text fw={600}>{t('merchantDetail.payokConnection')}</Text>
            <Switch
              label={t('merchantDetail.payokEnable')}
              description={t('merchantDetail.payokEnableDesc')}
              disabled={!canEditMerchant}
              {...form.getInputProps('enabled', { type: 'checkbox' })}
            />
            {canEditMerchant && (
              <Group justify="flex-end">
                <Button
                  type="submit"
                  loading={mutation.isPending}
                  leftSection={<IconDeviceFloppy size={14} />}
                >
                  {t('merchantDetail.payokSaveSettings')}
                </Button>
              </Group>
            )}
          </Stack>
        </Card>
      </form>
    </Stack>
  );
}

/**
 * Per-merchant cash-in payment-method allow-list. Internal staff toggle which of
 * the platform-available methods this merchant may use. Read-only (toggles
 * disabled, no save) when the viewer only holds `merchants.payment_methods.view`.
 */
function MerchantPaymentMethodsPanel({
  merchantId,
  canManage,
}: {
  merchantId: string;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['merchant-payment-methods', merchantId],
    queryFn: () => merchantsApi.getPaymentMethods(merchantId),
  });
  const [selected, setSelected] = useState<Set<string> | null>(null);

  // Server-resolved current selection (enabled===null => inherit => all available).
  const current = useMemo(() => {
    if (!data) return null;
    return new Set(data.enabled === null ? data.available : data.effective);
  }, [data]);
  const sel = selected ?? current ?? new Set<string>();

  const mutation = useMutation({
    meta: { successKey: 'notify.merchant.save.ok', errorKey: 'notify.merchant.save.fail' },
    mutationFn: () => {
      const arr = data!.available.filter((m) => sel.has(m));
      // All selected => store null (inherit) so future catalogue additions apply.
      const body = arr.length === data!.available.length ? null : arr;
      return merchantsApi.setPaymentMethods(merchantId, body);
    },
    onSuccess: () => {
      setSelected(null);
      queryClient.invalidateQueries({ queryKey: ['merchant-payment-methods', merchantId] });
    },
  });

  if (isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }
  if (error) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />}>
        {describeError(error)}
      </Alert>
    );
  }
  if (!data) return null;

  const inheriting = data.enabled === null;
  const toggle = (m: string) => {
    if (!canManage) return;
    setSelected((prev) => {
      const next = new Set(prev ?? current ?? new Set<string>());
      if (next.has(m)) next.delete(m);
      else next.add(m);
      return next;
    });
  };

  return (
    <Stack gap="md">
      {!canManage && (
        <Alert color="blue" icon={<IconEye size={16} />} variant="light">
          {t('merchantDetail.pmReadOnly')}
        </Alert>
      )}
      <Alert color={inheriting ? 'gray' : 'green'} variant="light">
        {inheriting
          ? t('merchantDetail.pmInheriting')
          : t('merchantDetail.pmRestricted', { n: data.effective.length })}
      </Alert>
      <Card>
        <Stack gap="xs">
          <Text fw={600}>{t('merchantDetail.pmTitle')}</Text>
          <Text size="xs" c="dimmed">
            {t('merchantDetail.pmDesc')}
          </Text>
          {/* Aligned grid — one method per cell, up to 6 columns on wide
              screens, degrading gracefully on narrower viewports. */}
          <SimpleGrid
            cols={{ base: 2, xs: 3, sm: 4, md: 5, lg: 6 }}
            spacing="sm"
            verticalSpacing="sm"
            mt="xs"
          >
            {data.available.map((m) => (
              <Switch
                key={m}
                label={m}
                size="sm"
                checked={sel.has(m)}
                disabled={!canManage}
                onChange={() => toggle(m)}
                styles={{ labelWrapper: { minWidth: 0 }, label: { whiteSpace: 'nowrap' } }}
              />
            ))}
          </SimpleGrid>
          {canManage && (
            <>
              {sel.size === 0 && (
                <Text size="xs" c="red">
                  {t('merchantDetail.pmNoneWarning')}
                </Text>
              )}
              <Group justify="flex-end" mt="sm">
                <Button variant="default" size="xs" onClick={() => setSelected(new Set(data.available))}>
                  {t('merchantDetail.pmSelectAll')}
                </Button>
                <Button
                  loading={mutation.isPending}
                  leftSection={<IconDeviceFloppy size={14} />}
                  onClick={() => mutation.mutate()}
                  disabled={sel.size === 0}
                >
                  {t('merchantDetail.pmSave')}
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}

const VA_CODES: { code: string; name: string }[] = [
  { code: 'BCAVA', name: 'BCA' },
  { code: 'BNIVA', name: 'BNI' },
  { code: 'BRIVA', name: 'BRI' },
  { code: 'MandiriVA', name: 'Mandiri' },
  { code: 'PermataVA', name: 'Permata' },
  { code: 'CIMBVA', name: 'CIMB' },
  { code: 'DanamonVA', name: 'Danamon' },
  { code: 'BSIVA', name: 'BSI' },
  { code: 'BNCVA', name: 'BNC' },
  { code: 'INAVA', name: 'INA' },
  { code: 'BNISyariahVA', name: 'BNI Syariah' },
  { code: 'ALTOVA', name: 'ALTO' },
  { code: 'SinarmasVA', name: 'Sinarmas' },
  { code: 'PrimaVA', name: 'Prima' },
  { code: 'MUAMALATVA', name: 'Muamalat' },
  { code: 'NobuVA', name: 'Nobu' },
];

/** A single VA code's draft fee: percentage (whole percent, e.g. 0.6) + flat IDR.
 *  Blank ('') = not configured for that component. */
type VaFeeDraft = { pct: number | string; fixed: number | string };
const EMPTY_VA_DRAFT: VaFeeDraft = { pct: '', fixed: '' };

/**
 * Per-Virtual-Account cash-in fee config. Each VA method charges a PERCENTAGE
 * (entered in %, stored as basis points) PLUS a FLAT IDR amount, both applied:
 * fee = round(amount × % ) + flat. Leaving BOTH fields blank means that VA falls
 * back to the merchant's percentage-only deposit fee. Fields are read-only unless
 * the viewer holds `merchants.edit`.
 */
function VaFeesPanel({ merchant, canEdit }: { merchant: MerchantDto; canEdit: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [fees, setFees] = useState<Record<string, VaFeeDraft>>(() => {
    const init: Record<string, VaFeeDraft> = {};
    for (const { code } of VA_CODES) {
      const cfg = merchant.vaFees?.[code];
      init[code] = cfg
        ? { pct: cfg.bps / 100, fixed: cfg.fixed }
        : { pct: '', fixed: '' };
    }
    return init;
  });
  const mutation = useMutation({
    meta: { successKey: 'notify.merchant.save.ok', errorKey: 'notify.merchant.save.fail' },
    mutationFn: () => {
      const map: Record<string, { bps: number; fixed: number }> = {};
      for (const { code } of VA_CODES) {
        const { pct, fixed } = fees[code] ?? EMPTY_VA_DRAFT;
        const hasPct = typeof pct === 'number' && pct >= 0;
        const hasFixed = typeof fixed === 'number' && fixed >= 0;
        // A VA is configured only if at least one component is set; a blank
        // component defaults to 0 so "0.6% only" or "3500 flat only" both work.
        if (hasPct || hasFixed) {
          map[code] = {
            bps: hasPct ? Math.round((pct as number) * 100) : 0,
            fixed: hasFixed ? (fixed as number) : 0,
          };
        }
      }
      return merchantsApi.setVaFees(merchant.id, Object.keys(map).length ? map : null);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['merchant', merchant.id] }),
  });
  return (
    <Stack gap="md">
      <Alert color="blue" icon={<IconCoin size={16} />} variant="light">
        {t('merchantDetail.vaFeesDesc')}
      </Alert>
      <Card>
        <Grid>
          {VA_CODES.map(({ code, name }) => (
            <Grid.Col key={code} span={{ base: 12, sm: 6, md: 4 }}>
              <Stack gap={4}>
                <Text size="sm" fw={500}>
                  {name} ({code})
                </Text>
                <Group gap="xs" grow wrap="nowrap">
                  <NumberInput
                    aria-label={`${name} ${t('merchantDetail.vaFeesPercentLabel')}`}
                    placeholder={t('merchantDetail.vaFeesPercentLabel')}
                    suffix=" %"
                    min={0}
                    max={100}
                    step={0.1}
                    decimalScale={2}
                    disabled={!canEdit}
                    value={fees[code]?.pct ?? ''}
                    onChange={(v) =>
                      setFees((f) => ({
                        ...f,
                        [code]: { ...(f[code] ?? EMPTY_VA_DRAFT), pct: typeof v === 'number' ? v : '' },
                      }))
                    }
                  />
                  <NumberInput
                    aria-label={`${name} ${t('merchantDetail.vaFeesFixedLabel')}`}
                    placeholder={t('merchantDetail.vaFeesFixedLabel')}
                    prefix="Rp "
                    min={0}
                    thousandSeparator
                    disabled={!canEdit}
                    value={fees[code]?.fixed ?? ''}
                    onChange={(v) =>
                      setFees((f) => ({
                        ...f,
                        [code]: { ...(f[code] ?? EMPTY_VA_DRAFT), fixed: typeof v === 'number' ? v : '' },
                      }))
                    }
                  />
                </Group>
              </Stack>
            </Grid.Col>
          ))}
        </Grid>
        {canEdit && (
          <Group justify="flex-end" mt="md">
            <Button
              loading={mutation.isPending}
              leftSection={<IconDeviceFloppy size={14} />}
              onClick={() => mutation.mutate()}
            >
              {t('merchantDetail.vaFeesSave')}
            </Button>
          </Group>
        )}
      </Card>
    </Stack>
  );
}

// Beneficiary account-type domain for per-type flat disbursement fees. Mirrors
// the payok_bank_codes.account_type values (BANK / WALLET / BPR). Display names
// are literal (like VaFeesPanel bank names) to keep t() keys static.
const DISBURSEMENT_TYPES: { type: string; name: string }[] = [
  { type: 'BANK', name: 'Bank' },
  { type: 'WALLET', name: 'E-wallet' },
  { type: 'BPR', name: 'BPR' },
];

/**
 * Per-account-TYPE cash-out fee config. Each account type charges a PERCENTAGE
 * (entered in %, stored as basis points) PLUS a FLAT IDR amount, both applied on
 * the amount: fee = round(amount × %) + flat. Leaving BOTH fields blank means that
 * account type falls back to the merchant's flat default disbursementFeeIdr. The
 * fee is ADDED at cash-out (debit = amount + fee); the beneficiary still receives
 * the full amount. Fields are read-only unless the viewer holds `merchants.edit`.
 */
function DisbursementFeesPanel({ merchant, canEdit }: { merchant: MerchantDto; canEdit: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [fees, setFees] = useState<Record<string, VaFeeDraft>>(() => {
    const init: Record<string, VaFeeDraft> = {};
    for (const { type } of DISBURSEMENT_TYPES) {
      const cfg = merchant.disbursementFees?.[type];
      init[type] = cfg ? { pct: cfg.bps / 100, fixed: cfg.fixed } : { pct: '', fixed: '' };
    }
    return init;
  });
  const mutation = useMutation({
    meta: { successKey: 'notify.merchant.save.ok', errorKey: 'notify.merchant.save.fail' },
    mutationFn: () => {
      const map: Record<string, { bps: number; fixed: number }> = {};
      for (const { type } of DISBURSEMENT_TYPES) {
        const { pct, fixed } = fees[type] ?? EMPTY_VA_DRAFT;
        const hasPct = typeof pct === 'number' && pct >= 0;
        const hasFixed = typeof fixed === 'number' && fixed >= 0;
        // A type is configured only if at least one component is set; a blank
        // component defaults to 0 so "10% only" or "5000 flat only" both work.
        if (hasPct || hasFixed) {
          map[type] = {
            bps: hasPct ? Math.round((pct as number) * 100) : 0,
            fixed: hasFixed ? (fixed as number) : 0,
          };
        }
      }
      return merchantsApi.setDisbursementFees(merchant.id, Object.keys(map).length ? map : null);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['merchant', merchant.id] }),
  });
  return (
    <Stack gap="md">
      <Alert color="blue" icon={<IconCoin size={16} />} variant="light">
        {t('merchantDetail.disbursementFeesDesc')}
      </Alert>
      <Card>
        <Grid>
          {DISBURSEMENT_TYPES.map(({ type, name }) => (
            <Grid.Col key={type} span={{ base: 12, sm: 6, md: 4 }}>
              <Stack gap={4}>
                <Text size="sm" fw={500}>
                  {name} ({type})
                </Text>
                <Group gap="xs" grow wrap="nowrap">
                  <NumberInput
                    aria-label={`${name} ${t('merchantDetail.vaFeesPercentLabel')}`}
                    placeholder={t('merchantDetail.vaFeesPercentLabel')}
                    suffix=" %"
                    min={0}
                    max={100}
                    step={0.1}
                    decimalScale={2}
                    disabled={!canEdit}
                    value={fees[type]?.pct ?? ''}
                    onChange={(v) =>
                      setFees((f) => ({
                        ...f,
                        [type]: { ...(f[type] ?? EMPTY_VA_DRAFT), pct: typeof v === 'number' ? v : '' },
                      }))
                    }
                  />
                  <NumberInput
                    aria-label={`${name} ${t('merchantDetail.vaFeesFixedLabel')}`}
                    placeholder={t('merchantDetail.vaFeesFixedLabel')}
                    prefix="Rp "
                    min={0}
                    thousandSeparator
                    disabled={!canEdit}
                    value={fees[type]?.fixed ?? ''}
                    onChange={(v) =>
                      setFees((f) => ({
                        ...f,
                        [type]: { ...(f[type] ?? EMPTY_VA_DRAFT), fixed: typeof v === 'number' ? v : '' },
                      }))
                    }
                  />
                </Group>
              </Stack>
            </Grid.Col>
          ))}
        </Grid>
        {canEdit && (
          <Group justify="flex-end" mt="md">
            <Button
              loading={mutation.isPending}
              leftSection={<IconDeviceFloppy size={14} />}
              onClick={() => mutation.mutate()}
            >
              {t('merchantDetail.disbursementFeesSave')}
            </Button>
          </Group>
        )}
      </Card>
    </Stack>
  );
}

/**
 * Server-to-server disbursement (money-out via API key) gate. HIGH-RISK toggle —
 * only rendered for internal staff holding `merchants.api_disbursement.manage`.
 */
function ApiDisbursementCard({ merchant }: { merchant: MerchantDto }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(merchant.apiDisbursementEnabled);
  const mutation = useMutation({
    meta: { successKey: 'notify.merchant.save.ok', errorKey: 'notify.merchant.save.fail' },
    mutationFn: (next: boolean) => merchantsApi.setApiDisbursement(merchant.id, next),
    onSuccess: (_d, next) => {
      setEnabled(next);
      queryClient.invalidateQueries({ queryKey: ['merchant', merchant.id] });
    },
  });
  return (
    <Card mt="md">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={600}>{t('merchantDetail.apiDisbTitle')}</Text>
            <Text size="xs" c="dimmed">
              {t('merchantDetail.apiDisbDesc')}
            </Text>
          </div>
          <Switch
            checked={enabled}
            disabled={mutation.isPending}
            onChange={(e) => mutation.mutate(e.currentTarget.checked)}
          />
        </Group>
        <Alert color="orange" icon={<IconAlertCircle size={16} />} variant="light">
          {t('merchantDetail.apiDisbWarning')}
        </Alert>
      </Stack>
    </Card>
  );
}

function WalletPanel({ merchantId }: { merchantId: string }) {
  const { t } = useTranslation();
  const { has, isSuperuser } = usePermissions();
  const canAdjust = isSuperuser || has('wallet.internal.adjust');
  const [adjustOpen, setAdjustOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['wallet', merchantId],
    queryFn: () => walletApi.get(merchantId),
    refetchInterval: 5000,
    retry: 0,
  });
  if (isLoading) {
    return (
      <Group justify="center" py="lg">
        <Loader />
      </Group>
    );
  }
  if (error || !data) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />}>
        {describeError(error)}
      </Alert>
    );
  }
  return (
    <Card>
      <Group justify="space-between" mb="md">
        <Text fw={600}>{t('merchantDetail.balances')}</Text>
        {canAdjust && (
          <Button size="xs" variant="default" leftSection={<IconWallet size={14} />} onClick={() => setAdjustOpen(true)}>
            {t('merchantDetail.adjustActiveBalance')}
          </Button>
        )}
      </Group>
      <Grid>
        <Field label={t('merchantDetail.balPending')} value={formatIdr(data.pendingBalance)} />
        <Field label={t('merchantDetail.balActive')} value={formatIdr(data.activeBalance)} />
        <Field label={t('merchantDetail.balReserved')} value={formatIdr(data.reservedBalance)} />
        <Field
          label={t('merchantDetail.balOutstanding')}
          value={
            data.outstandingBalance > 0
              ? t('merchantDetail.balOutstandingValue', { amount: formatIdr(data.outstandingBalance) })
              : formatIdr(0)
          }
        />
        <Field label={t('merchantDetail.balLifetimeDeposit')} value={formatIdr(data.lifetimeDeposit)} />
        <Field label={t('merchantDetail.balLifetimeDisbursement')} value={formatIdr(data.lifetimeDisbursement)} />
        <Field label={t('merchantDetail.balLifetimeFee')} value={formatIdr(data.lifetimeFee)} />
        <Field label={t('merchantDetail.balUpdated')} value={formatGmt7(data.updatedAt)} />
      </Grid>
      {canAdjust && adjustOpen && (
        <AdjustBalanceModal merchantId={merchantId} wallet={data} onClose={() => setAdjustOpen(false)} />
      )}
    </Card>
  );
}

/**
 * Internal-only modal to add or deduct a merchant's active balance.
 * Deducting more than the available active balance is allowed only when the
 * operator opts into carry-over, which defers the shortfall to future
 * settlements (tracked in outstanding_balance).
 */
function AdjustBalanceModal({
  merchantId,
  wallet,
  onClose,
}: {
  merchantId: string;
  wallet: WalletDto;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [direction, setDirection] = useState<'add' | 'deduct'>('add');
  const [amount, setAmount] = useState<number | ''>('');
  const [carryover, setCarryover] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const amt = typeof amount === 'number' ? amount : 0;
  // For a deduct, how much exceeds active and would need to be carried over.
  const shortfall = direction === 'deduct' ? Math.max(0, amt - wallet.activeBalance) : 0;
  const needsCarryover = shortfall > 0;

  const mut = useMutation({
    // Money modal: shows its own detailed success toast (via the shared notify
    // helper, so the design matches) + inline error for the operator. Opt out of
    // both global toasts to avoid duplicates.
    meta: { successNotification: false, errorNotification: false },
    mutationFn: () =>
      walletApi.adjustActive(merchantId, {
        direction,
        amount: amt,
        carryover: needsCarryover ? carryover : false,
        reason: reason.trim(),
      }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['wallet', merchantId] });
      notifySuccess(
        res.carriedOver > 0
          ? translateStatic('notify.balance.adjustCarry', {
              active: formatIdr(res.newActiveBalance),
              carried: formatIdr(res.carriedOver),
              outstanding: formatIdr(res.newOutstandingBalance),
            })
          : translateStatic('notify.balance.adjustOk', { active: formatIdr(res.newActiveBalance) }),
        translateStatic('notify.balance.adjustTitle'),
      );
      onClose();
    },
    onError: (err) => setError(describeError(err)),
  });

  const reasonValid = reason.trim().length >= 10;
  const amountValid = amt > 0;
  // Block a deduct that overflows active unless carry-over is explicitly enabled.
  const blockedByCarryover = needsCarryover && !carryover;
  const canSubmit = amountValid && reasonValid && !blockedByCarryover && !mut.isPending;

  return (
    <Modal opened onClose={onClose} title={t('merchantDetail.adjustActiveBalance')} centered>
      <Stack>
        <Text size="sm" c="dimmed">
          {t('merchantDetail.adjustCurrentActive')}<b>{formatIdr(wallet.activeBalance)}</b>
          {wallet.outstandingBalance > 0 && (
            <>{t('merchantDetail.adjustOutstanding')}<b>{formatIdr(wallet.outstandingBalance)}</b></>
          )}
        </Text>

        <SegmentedControl
          fullWidth
          value={direction}
          onChange={(v) => {
            setDirection(v as 'add' | 'deduct');
            setError(null);
          }}
          data={[
            { label: t('merchantDetail.adjustAdd'), value: 'add' },
            { label: t('merchantDetail.adjustDeduct'), value: 'deduct' },
          ]}
        />

        <NumberInput
          label={t('merchantDetail.adjustAmount')}
          placeholder="0"
          min={1}
          thousandSeparator="."
          // Must differ from thousandSeparator or Mantine NumberInput throws on
          // render ("Decimal separator can't be same as thousand separator") and
          // blanks the page. decimalScale={0} disallows decimals anyway; this is
          // just the Indonesian "." thousands / "," decimals convention.
          decimalSeparator=","
          decimalScale={0}
          allowNegative={false}
          value={amount}
          onChange={(v) => setAmount(typeof v === 'number' ? v : '')}
        />

        {needsCarryover && (
          <Alert color="yellow" icon={<IconAlertCircle size={16} />} variant="light">
            {t('merchantDetail.adjustExceedsPre')}<b>{formatIdr(shortfall)}</b>.{' '}
            {t('merchantDetail.adjustExceedsMid', { deducted: formatIdr(Math.min(amt, wallet.activeBalance)) })}
            {formatIdr(shortfall)}{t('merchantDetail.adjustExceedsPost')}
            <Checkbox
              mt="sm"
              checked={carryover}
              onChange={(e) => setCarryover(e.currentTarget.checked)}
              label={t('merchantDetail.adjustCarryLabel', { amount: formatIdr(shortfall) })}
            />
          </Alert>
        )}

        <Textarea
          label={t('merchantDetail.adjustReason')}
          description={t('merchantDetail.adjustReasonDesc')}
          placeholder={t('merchantDetail.adjustReasonPlaceholder')}
          autosize
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          error={reason.length > 0 && !reasonValid ? t('merchantDetail.adjustReasonError') : undefined}
        />

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} variant="light">
            {error}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={mut.isPending}>
            {t('merchantDetail.cancel')}
          </Button>
          <Button
            color={direction === 'deduct' ? 'red' : undefined}
            loading={mut.isPending}
            disabled={!canSubmit}
            onClick={() => {
              setError(null);
              mut.mutate();
            }}
          >
            {direction === 'add' ? t('merchantDetail.adjustAddToActive') : t('merchantDetail.adjustDeductFromActive')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
